<template>
  <main id="main" class="d-flex flex-column h-100vh">
    <div class="header_page d-flex justify-content-end align-items-center column-gap-3 shadow-sm bg_white z-index-10">
        <p id="title_header_page">{{ studentName }}</p>
        <img src="@/assets/imgs/back.svg" class="icon-size-3 cursor-pointer" alt="" @click="$router.go(-1)">
    </div>

    <div ref="chatContainer" class="p-3 d-flex flex-column row-gap-3 flex-grow-1 overflow-auto pb-custom bg_gray_light">
        <div v-if="loading && messages.length === 0" class="text-center mt-5">
            <p>{{ t('loading') }}</p>
        </div>

        <div v-else-if="messages.length === 0" class="text-center mt-5 text_color_gray">
            {{ t('no_chat') }}
        </div>

        <div v-else v-for="msg in messages" :key="msg.id" class="w-100 d-flex" :class="msg.teacher_side ? 'justify-content-end' : 'justify-content-start'">
            <section 
                class="shadow p-3 rounded-4 message-box" 
                :class="msg.teacher_side ? 'bg_sky_blue text-dark rounded-br-0' : 'bg-white text-dark rounded-bl-0'"
                style="max-width: 80%;"
            >
                <div class="d-flex justify-content-between align-items-center column-gap-3 border-bottom-custom pb-2 mb-2">
                    <span class="font_tiny bold">{{ msg.date }}</span>
                    <span class="font_tiny bold">{{ msg.time }}</span>
                </div>
                <p class="font_normal bold text-break">
                    {{ msg.text }}
                </p>
                <div v-if="msg.teacher_side" class="text-end mt-1">
                    <span class="font_tiny text_color_gray">✔ {{ t('sent') }}</span>
                </div>
            </section>
        </div>
    </div>

    <div class="p-3 bg_white shadow-top position-fixed bottom-0 w-100 z-index-10">
        <div class="d-flex align-items-center gap-2 max_width mx-auto">
            <input 
                v-model="newMessage" 
                @keyup.enter="sendMessage"
                type="text" 
                class="flex-grow-1 p-3 rounded-pill border_gray bg_gray_input" 
                :placeholder="t('type_message')"
            >
            <button class="button rounded-circle p-0 d-flex justify-content-center align-items-center send-btn" @click="sendMessage" :disabled="sending">
                <svg v-if="!sending" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.01 21L23 12 2.01 3 2 10L17 12L2 14L2.01 21Z" fill="white"/>
                </svg>
                <div v-else class="spinner-border spinner-border-sm text-white" role="status"></div>
            </button>
        </div>
    </div>
  </main>
</template>

<script>
import apiClient, { createFormData } from '@/services/api'

export default {
  name: 'TeacherChatDetail',
  data() {
    return {
        chatId: null,
        studentName: '',
        messages: [],
        loading: false,
        newMessage: '',
        sending: false,
        pollingInterval: null
    }
  },
  mounted() {
    this.chatId = this.$route.query.chat_id;
    this.studentName = this.$route.query.student_name || this.t('student_name');
    
    if (this.chatId) {
        this.fetchMessages();
        this.pollingInterval = setInterval(() => {
            this.fetchMessages(true);
        }, 5000);
    }
  },
  beforeUnmount() {
    if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
    }
  },
  methods: {
    async fetchMessages(isBackground = false) {
        if (!isBackground) {
            this.loading = true;
        }
        try {
            const userId = localStorage.getItem('user_id');
            const formData = createFormData({
                teacher_id: userId,
                chat_id: this.chatId
            });

            const response = await apiClient.post('/teacher/details_chat', formData);

            if (response.data.status === 'ok' && response.data.data) {
                const prevLength = this.messages.length;
                this.messages = response.data.data;
                if (this.messages.length > prevLength) {
                    this.scrollToBottom();
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            if (!isBackground) {
                this.loading = false;
                this.scrollToBottom();
            }
        }
    },
    async sendMessage() {
        if (!this.newMessage.trim()) return;

        this.sending = true;
        try {
            const userId = localStorage.getItem('user_id');
            const formData = createFormData({
                teacher_id: userId,
                chat_id: this.chatId,
                text: this.newMessage
            });

            const response = await apiClient.post('/teacher/add_chat', formData);

            if (response.data.status === 'ok') {
                this.newMessage = '';
                this.fetchMessages(true); 
            } else {
                this.notify(response.data.message || this.t('error_submit'), 'error');
            }
        } catch (error) {
            console.error(error);
            this.notify(this.t('connection_error'), 'error');
        } finally {
            this.sending = false;
        }
    },
    scrollToBottom() {
        this.$nextTick(() => {
            const container = this.$refs.chatContainer;
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        });
    }
  }
}
</script>

<style scoped>
.h-100vh {
    height: 100vh;
}
.cursor-pointer {
    cursor: pointer;
}
.bg_gray_light {
    background-color: #f5f5f5;
}
.bg_sky_blue {
    background-color: #E0F7FA; 
}
.border-bottom-custom {
    border-bottom: 1px solid rgba(0,0,0,0.1);
}
.rounded-bl-0 {
    border-bottom-left-radius: 0 !important;
}
.rounded-br-0 {
    border-bottom-right-radius: 0 !important;
}
.shadow-top {
    box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
}
.z-index-10 {
    z-index: 10;
}
.border_gray {
    border: 1px solid #e0e0e0;
}
.bg_gray_input {
    background-color: #fafafa;
}
.send-btn {
    width: 3.5rem;
    height: 3.5rem;
}
.font_tiny {
    font-size: 0.7rem;
}
.message-box {
    transition: all 0.3s ease;
}
.pb-custom {
    padding-bottom: 9rem !important;
}
</style>